import { YES_NO } from 'components/v2/compiler/constants';
import { AdminToolSearchRequest } from 'types/admin-tools/AdminToolSearchRequest';
import { ToolType } from 'types/tools/ToolType';
import { ToolHeadState } from 'types/tools/ToolHeadState';

enum QueryKey {
  NAME = 'q',
  TOOL_TYPE = 't',
  PCR = 'pcr',
  CPCR = 'c-pcr',
  HAS_API_INTEGRATION = 'api',
  NUMBER_OF_PUBLISHED_EPDS = 'epds',
  HEAD_STATE = 's',
  TOOL_OWNER = 'tool-owner',
  PAGE_NUMBER = 'page',
  PAGE_SIZE = 'pageSize',
}

export const PAGE_SIZES = [10, 20, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];

const AdminSearchToolsQueryHelper = {
  parse(query: string): AdminToolSearchRequest {
    const searchParams = new URLSearchParams(query);

    return {
      name: searchParams.get(QueryKey.NAME) || '',
      toolType: getEnumOrNull(searchParams, QueryKey.TOOL_TYPE, ToolType),
      pcrId: searchParams.get(QueryKey.PCR) || '',
      cpcrId: searchParams.get(QueryKey.CPCR) || '',
      hasApiIntegration: getEnumOrNull(searchParams, QueryKey.HAS_API_INTEGRATION, YES_NO),
      numberOfPublishedEpds: searchParams.get(QueryKey.NUMBER_OF_PUBLISHED_EPDS) || null,
      headState: getEnumOrNull(searchParams, QueryKey.HEAD_STATE, ToolHeadState),
      toolOwnerId: searchParams.get(QueryKey.TOOL_OWNER) || null,
      pageNumber: getIntOrDefault(searchParams, QueryKey.PAGE_NUMBER, 1),
      pageSize: getIntOrDefault(searchParams, QueryKey.PAGE_SIZE, DEFAULT_PAGE_SIZE),
    };
  },

  stringify(param: AdminToolSearchRequest): string {
    const searchParams = new URLSearchParams();

    if (param.name) {
      searchParams.append(QueryKey.NAME, param.name);
    }
    if (param.toolType) {
      searchParams.append(QueryKey.TOOL_TYPE, param.toolType);
    }
    if (param.pcrId) {
      searchParams.append(QueryKey.PCR, param.pcrId);
    }
    if (param.cpcrId) {
      searchParams.append(QueryKey.CPCR, param.cpcrId);
    }
    if (param.hasApiIntegration) {
      searchParams.append(QueryKey.HAS_API_INTEGRATION, param.hasApiIntegration);
    }
    if (param.numberOfPublishedEpds) {
      searchParams.append(QueryKey.NUMBER_OF_PUBLISHED_EPDS, param.numberOfPublishedEpds);
    }
    if (param.headState) {
      searchParams.append(QueryKey.HEAD_STATE, param.headState);
    }
    if (param.toolOwnerId) {
      searchParams.append(QueryKey.TOOL_OWNER, param.toolOwnerId);
    }
    if (param.pageNumber > 1) {
      searchParams.append(QueryKey.PAGE_NUMBER, String(param.pageNumber));
    }
    if (param.pageSize !== DEFAULT_PAGE_SIZE) {
      searchParams.append(QueryKey.PAGE_SIZE, String(param.pageSize));
    }

    return searchParams.size > 0 ? `?${searchParams.toString()}` : '';
  },

  isFilterActive(param: AdminToolSearchRequest): boolean {
    return AdminSearchToolsQueryHelper.stringify({ ...param, pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE }) !== '';
  },
};
export default AdminSearchToolsQueryHelper;

function getEnumOrNull<T extends Record<string, string>>(
  query: URLSearchParams,
  key: string,
  enumObj: T
): T[keyof T] | null {
  const value = query.get(key);
  if (!value) {
    return null;
  }

  const possibleValues = Object.values(enumObj);
  if (!possibleValues.includes(value)) {
    return null;
  }

  return value as T[keyof T];
}

function getIntOrDefault(query: URLSearchParams, key: string, defaultValue: number): number {
  const value = query.get(key);
  if (!value) {
    return defaultValue;
  }
  const asInt = Number(value);
  if (!Number.isSafeInteger(asInt)) {
    return defaultValue;
  }
  return asInt;
}
